/**
 * The landing shell: what a first visit to the landing (or About) renders —
 * the landing's routes and the little chrome they need, without the signed-in
 * app. The app (sidebar, chat, editors, the sync engine, every other route)
 * is `app-shell.tsx`, a separate chunk; before this split every visitor to
 * `/` downloaded and parsed all of it (≈4 MB compressed) to see the landing.
 */
import { type ReactNode, Suspense, useEffect, useMemo } from 'react';
import { Route, useLocation, useNavigate } from 'react-router';

import { useAuth } from '@/auth/better/session';
import { authDialogs, useAuthDialogs } from '@/auth/better/store';
import { stripLangPrefix } from '@/seo/marketing-routes';

import { ToasterSonner } from './components/ui/sonner';
import { ErrorBoundary } from './Error';
import { useRTL } from './hooks/use-rtl';
import { LangBoundary } from './i18n/LangBoundary';
import { renderLandingShellRoutes, RootRedirect } from './landing-routes';
import { AutoUpdater, OutageBar, SentryRoutes, TOAST_OPTIONS } from './shell-common';
import { PaymentDialogContext } from './upgrade/upgrade-dialog-context';

/** Fetch the app shell ahead of need: once the visitor is signing in (the
 *  auth dialog is open) or already signed in — the two moments the next page
 *  is almost certainly inside the app. */
function usePrefetchAppShell(prefetch: () => void) {
  const { open } = useAuthDialogs();
  const { isSignedIn } = useAuth();
  useEffect(() => {
    if (open || isSignedIn) prefetch();
  }, [open, isSignedIn, prefetch]);
}

/**
 * The landing's live demos are the app's own components, and some of them can
 * ask for the plans dialog (an AI action out of credits, a premium export).
 * That dialog lives in the app shell; here a visitor is offered the sign-up
 * instead, and a signed-in one goes to the app's upgrade page.
 */
function LandingPaymentDialog({ children }: { children: ReactNode }) {
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const value = useMemo(
    () => ({
      showPaymentDialog: () =>
        isSignedIn ? navigate('/upgrade') : authDialogs.openSignUp(),
      closePaymentDialog: () => {},
    }),
    [isSignedIn, navigate],
  );
  return (
    <PaymentDialogContext.Provider value={value}>
      {children}
    </PaymentDialogContext.Provider>
  );
}

export function LandingShell({ prefetchApp }: { prefetchApp: () => void }) {
  const { isRTL } = useRTL();
  usePrefetchAppShell(prefetchApp);
  // The v3 landing paints its own dot lattice on a fixed layer and covers this
  // wrapper entirely; here a `background-attachment: fixed` it never shows
  // would still put Safari's whole scroll on the main thread.
  const { pathname } = useLocation();
  const onV3Landing = ['/', '/home'].includes(
    stripLangPrefix(pathname).replace(/\/$/, '') || '/',
  );

  return (
    <OutageBar>
      <ToasterSonner toastOptions={TOAST_OPTIONS} />
      <AutoUpdater />
      {/* The same surface, and the same two boxes, the app shell gives a
          landing page (its Layout + the sidebar wrapper with the sidebar
          hidden), so a page lays out identically in either shell. */}
      <div
        className={`h-full w-full bg-background bg-dotted-pattern bg-dotted-size ${onV3Landing ? '' : 'bg-fixed'} dark:bg-dotted-pattern-dark ${isRTL ? 'rtl' : ''}`}
        dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="flex h-full w-full flex-row">
          <div className="mt-0 flex min-h-0 w-full flex-1 flex-col items-center">
            <ErrorBoundary>
              <LandingPaymentDialog>
                <Suspense fallback={null}>
                  <SentryRoutes>
                    <Route path="/" element={<RootRedirect />} />
                    <Route element={<LangBoundary lang="en" />}>
                      {renderLandingShellRoutes(false)}
                    </Route>
                    <Route path=":lang" element={<LangBoundary />}>
                      {renderLandingShellRoutes(true)}
                    </Route>
                  </SentryRoutes>
                </Suspense>
              </LandingPaymentDialog>
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </OutageBar>
  );
}
